import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import { ReactComponent as jumpcloudLogo } from "../logos/jumpcloud.svg";
import add_new_image from "./images/jumpcloud_add_new.png";
import drop_down_image from "./images/jumpcloud_drop_down.png";
import generate_key_image from "./images/jumpcloud_generate_key.png";
import role_and_api_image from "./images/jumpcloud_role_and_api.png";

// Cannot import from integrationsMapping due to circular dependency
const JUMPCLOUD_CODE_NAME = "jumpcloud";

export default function JumpCloudIntegration() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const { registerJumpCloudIntegration } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated(JUMPCLOUD_CODE_NAME);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const guideMode = searchParams.has("viewonly");

  const onSubmit = async (values) => {
    setRegisterIsLoading(true);
    try {
      await registerJumpCloudIntegration(apiKey);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("JumpCloud registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={isIntegrated ? "" : "JumpCloud integration instructions"}
        image={jumpcloudLogo}
        iconBorderRadius={0}
        name={JUMPCLOUD_CODE_NAME}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>JumpCloud is registered!</Typography>
            </Box>
          ) : (
            <Box>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Create a new Read Only Administrator">
                  <Typography variant="body2">
                    A dedicated email address is required to complete the new
                    user creation.
                    <br />
                    This user will not be used to log in to the console, so no
                    need to keep the credentials once the process is complete.
                  </Typography>
                  <TextWithExternalLink
                    text="Please enter the JumpCloud admin console and go to the"
                    linkText="Administrators settings"
                    href="https://console.jumpcloud.com/#/settings/administrators"
                  />
                  <Typography variant="body2">
                    Create a new user clicking the "+ Admin" and selecting "As
                    New"
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_new_image,
                        altext: "add new admin",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    <br />
                    Fill the form in the "Create New Administrator" drawer.
                    <br />
                    Choose the "Read Only" role and check "Enable API access".
                    <br />
                    Click Save.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: role_and_api_image,
                        altext: "read only",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    <br />
                    Confirm the email sent by JumpCloud to the new admin.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create an API Key">
                  <TextWithExternalLink
                    text="With the new Read Only admin account, login to the"
                    linkText="JumpCloud admin console"
                    href="https://console.jumpcloud.com/login/admin"
                  />
                  <Typography variant="body2">
                    Click the username drop down located in the top-right of the
                    Console, and click "My API Key".
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: drop_down_image,
                        altext: "drop down",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    <br />
                    In the API Key window, choose no expiration date and
                    generate a new API key.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: generate_key_image,
                        altext: "generate key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Enter the API Key">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <TextField
                      placeholder="API key"
                      fullWidth
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle api key visibility"
                              onClick={() => setShowApiKey((prev) => !prev)}
                            >
                              {showApiKey ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ContainedButton
                      onClick={onSubmit}
                      size="small"
                      color="primary"
                      disabled={guideMode}
                      loading={registerIsLoading}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
}
